import { Space } from "../ui";
import type { ReactNode } from "react";
import {
  MastercardBrandIcon,
  PaymentBrandBadge,
  PayPalBrandIcon,
  SepaBrandIcon,
  PaymentBrandIcon,
  VisaBrandIcon,
} from "./paymentBrandIcons";
import { spacing } from "../layout/spacing";

const paymentMethodIcons: Record<string, ReactNode> = {
  card: (
    <>
      <VisaBrandIcon style={{ width: "1.75em" }} />
      <MastercardBrandIcon style={{ width: "1.75em" }} />
    </>
  ),
  sepa_debit: <SepaBrandIcon />,
  link: <PaymentBrandIcon name="link" />,
  paypal: <PayPalBrandIcon />,
  amazon_pay: <PaymentBrandIcon name="amazon-pay" />,
  apple_pay: <PaymentBrandIcon name="apple-pay" />,
  google_pay: <PaymentBrandIcon name="googlepay" />,
};

// Provider IDs are technical keys. Display names and artwork never classify free text.
const methodBrands: Record<string, [name: string, artwork: string]> = {
  klarna: ["Klarna", "klarna"], affirm: ["Affirm", "affirm"],
  afterpay_clearpay: ["Afterpay / Clearpay", "afterpay"],
  bancontact: ["Bancontact", "bancontact-mister-cash"], ideal: ["iDEAL", "ideal"],
  eps: ["eps", "eps"], giropay: ["giropay", "giropay"], p24: ["Przelewy24", "p24"],
  blik: ["BLIK", "blik"], multibanco: ["Multibanco", "multibanco"], sofort: ["Sofort", "sofort"],
  alipay: ["Alipay", "alipay"], alipay_hk: ["AlipayHK", "alipay_hk"],
  wechat_pay: ["WeChat Pay", "wechat-pay"], cashapp: ["Cash App Pay", "cashapp"],
  revolut_pay: ["Revolut Pay", "revolut"], samsung_pay: ["Samsung Pay", "samsungpay"],
  mobilepay: ["MobilePay", "mobilepay"], twint: ["TWINT", "twint"],
  swish: ["Swish", "swish"], vipps: ["Vipps", "vipps"],
  grabpay: ["GrabPay", "grab"], paynow: ["PayNow", "paynow"], pix: ["Pix", "pix"],
  dana: ["DANA", "dana"], kakao_pay: ["Kakao Pay", "kakaopay"], naver_pay: ["Naver Pay", "naver"],
  fpx: ["FPX", "fpx"], oxxo: ["OXXO", "oxxo"], bpay: ["BPAY", "bpay"],
  us_bank_account: ["ACH", "bank-transfer"], acss_debit: ["ACSS", "direct-debit"],
  au_becs_debit: ["BECS", "direct-debit"], bacs_debit: ["Bacs", "direct-debit"],
  nz_bank_account: ["BECS NZ", "bank-transfer"], customer_balance: ["", "bank-transfer"],
  interac_present: ["Interac", "interac"], card_present: ["", "card"],
};

export function getPaymentMethodLabel(method: string, labels: Record<string, string>) {
  return labels[method] ?? (methodBrands[method]?.[0] || formatPaymentMethodKey(method));
}

export function renderPaymentMethodBadge(method: string): ReactNode {
  const icon = paymentMethodIcons[method] ?? <PaymentBrandIcon name={methodBrands[method]?.[1] ?? "cash"} />;
  return <PaymentBrandBadge>{icon}</PaymentBrandBadge>;
}

// These bundled assets contain the complete name. Other methods retain a text label.
const checkoutWordmarks: Record<string, string> = {
  paypal: "paypal", amazon_pay: "amazon-pay", apple_pay: "apple-pay",
  google_pay: "googlepay", link: "link",
};

export function renderCheckoutPaymentIdentity(method: string, label: string): ReactNode {
  const wordmark = checkoutWordmarks[method];
  return wordmark
    ? <PaymentBrandIcon name={wordmark} size="3em" style={{ width: "7em", maxWidth: "100%" }} />
    : <Space size={spacing.tightGap}>{renderPaymentMethodBadge(method)}<span>{label}</span></Space>;
}

export function renderPaymentMethodLabel(method: string, labels: Record<string, string>) {
  return (
    <Space size={spacing.tightGap}>
      {renderPaymentMethodBadge(method)}
      <span>{getPaymentMethodLabel(method, labels)}</span>
    </Space>
  );
}

function formatPaymentMethodKey(method: string) {
  return method
    .split("_")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}
